import { types, getSnapshot } from 'mobx-state-tree';
import { ImageElement } from './image-model';
import { TextElement } from './text-model';
import { SVGElement } from './svg-model';
import { FigureElement } from './figure-model';
import { LineElement } from './line-model';
import { Node } from './node-model';

export const TYPES_MAP = {
  image: ImageElement,
  text: TextElement,
  svg: SVGElement,
  figure: FigureElement,
  line: LineElement,
};

const ElementUnion = types.union(
  {
    dispatcher: (snapshot) => {
      if (!snapshot || !snapshot.type) return Node;
      const model = TYPES_MAP[snapshot.type];
      if (model) return model;
      // For group type or unknown types, use late-bound GroupElement or Node
      if (snapshot.type === 'group') return GroupElement;
      return Node;
    },
  },
  ImageElement,
  TextElement,
  SVGElement,
  FigureElement,
  LineElement,
  // GroupElement added below via late binding
  Node
);

export const GroupElement = Node.named('GroupElement')
  .props({
    type: types.optional(types.string, 'group'),
    x: types.optional(types.number, 0),
    y: types.optional(types.number, 0),
    width: types.optional(types.number, 0),
    height: types.optional(types.number, 0),
    rotation: types.optional(types.number, 0),
    draggable: types.optional(types.boolean, true),
    resizable: types.optional(types.boolean, true),
    children: types.optional(types.array(types.late(() => ElementUnion)), []),
  })
  .preProcessSnapshot((snapshot) => {
    if (!snapshot) return snapshot;
    const s = { ...snapshot };

    // Polotno compat: groups without explicit width — compute bounds from children
    if (s.children && s.children.length > 0 && !(s.width > 0)) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      s.children.forEach((child) => {
        const cx = child.x || 0;
        const cy = child.y || 0;
        const cw = child.width || 0;
        const ch = child.height || 0;
        minX = Math.min(minX, cx);
        minY = Math.min(minY, cy);
        maxX = Math.max(maxX, cx + cw);
        maxY = Math.max(maxY, cy + ch);
      });
      if (minX !== Infinity) {
        s.x = minX;
        s.y = minY;
        s.width = maxX - minX;
        s.height = maxY - minY;
        s.children = s.children.map((child) => ({
          ...child,
          x: (child.x || 0) - minX,
          y: (child.y || 0) - minY,
        }));
      }
    }

    return s;
  })
  .actions((self) => ({
    toJSON() {
      const gx = self.x;
      const gy = self.y;
      return {
        id: self.id,
        type: self.type,
        name: self.name,
        opacity: self.opacity,
        visible: self.visible,
        selectable: self.selectable,
        removable: self.removable,
        alwaysOnTop: self.alwaysOnTop,
        showInExport: self.showInExport,
        children: self.children.map((child) => {
          const cj = child.toJSON ? child.toJSON() : getSnapshot(child);
          return { ...cj, x: (cj.x || 0) + gx, y: (cj.y || 0) + gy };
        }),
      };
    },
  }));

export const forEveryChild = (node, cb) => {
  cb(node);
  if (node.children) {
    node.children.forEach((child) => forEveryChild(child, cb));
  }
};

export const createElement = (attrs) => {
  const type = attrs.type;
  const Model = TYPES_MAP[type];
  if (Model) {
    return Model.create(attrs);
  }
  if (type === 'group') {
    return GroupElement.create(attrs);
  }
  return Node.create(attrs);
};

export { ElementUnion };
